/**
 * useChatSession - wires the WebSocket client, reducer, and lifecycle together.
 *
 * One instance per AiChatPane mount. State and the socket are cleaned up on unmount.
 *
 * See context/ai-chat.md §7 for architecture reference.
 */

import type {
  ChatMode,
  ExplicitContextItem,
  GeminiModel,
  NavigationActionPayload,
  RunCompletedEvent,
  RunOrigin,
  SelectedContext,
  ServerEvent,
  UserPromptResponse,
} from '@protoss/shared';
import {
  derivePromptAnswer,
  deriveSelectedPreviews,
  isNavigationActionPayload,
  triggerSourceForAgentKey,
} from '@protoss/shared';
import * as React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { firebaseAuth } from '../../../lib/firebase';
import {
  apiCreateSession,
  apiListSessions,
  apiLoadSessionHistory,
  apiRenameSession,
  apiSetMessageActionResult,
  type ChatScope,
} from '../services/chatApiService';
import { ChatWsClient } from '../services/chatWsClient';
import { messagesToTurns } from '../services/historyHydrator';
import { chatReducer, type ChatActionType } from '../state/chatReducer';
import { initialChatState } from '../state/chatState';

// Resolve the WS base URL from the Vite-injected API URL env var.
// The API serves over HTTP; swap schema for WS.
function resolveWsBaseUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (apiUrl) {
    return apiUrl.replace(/^http/, 'ws').replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/^http/, 'ws').replace(/\/$/, '');
  }

  return 'ws://localhost:3001';
}

/** Resolve the API scope from optional projectUuid / workspaceUuid. Returns null if neither present. */
function resolveScope(projectUuid?: string, workspaceUuid?: string): ChatScope | null {
  if (projectUuid) return { type: 'project', projectUuid };
  if (workspaceUuid) return { type: 'workspace', workspaceUuid };
  return null;
}

/**
 * Merge explicit context items into SelectedContext fields for the backend.
 *
 * Converts typed ExplicitContextItem records into the flat SelectedContext
 * structure expected by the WebSocket protocol and AI backend. Items are additive
 * on top of the base context - they never override explicitly-supplied fields.
 */
function mergeExplicitItemsIntoContext(
  base: SelectedContext,
  items: ExplicitContextItem[]
): SelectedContext {
  if (items.length === 0) return base;

  const directories: string[] = [...(base.selectedDirectories ?? [])];
  const files: SelectedContext['selectedFiles'] = [...(base.selectedFiles ?? [])];
  const lineRanges: SelectedContext['selectedLineRanges'] = [...(base.selectedLineRanges ?? [])];

  for (const item of items) {
    switch (item.contextType) {
      case 'repo_directory':
        if (item.path && !directories.includes(item.path)) {
          directories.push(item.path);
        }
        break;
      case 'repo_file':
        if (item.path && !files.some((f) => f.path === item.path)) {
          files.push({ path: item.path, repositoryId: item.repoId });
        }
        break;
      case 'repo_file_range':
        if (item.path && item.lineStart != null && item.lineEnd != null) {
          lineRanges.push({
            filePath: item.path,
            lineStart: item.lineStart,
            lineEnd: item.lineEnd,
          });
        }
        break;
    }
  }

  return {
    ...base,
    ...(directories.length > 0 ? { selectedDirectories: directories } : {}),
    ...(files.length > 0 ? { selectedFiles: files } : {}),
    ...(lineRanges.length > 0 ? { selectedLineRanges: lineRanges } : {}),
  };
}

export interface SendRunOptions {
  prompt: string;
  context?: SelectedContext;
  model?: GeminiModel;
  mode?: ChatMode;
  /** Active module key at the time of the run. Drives agent selection in the orchestrator. */
  moduleKey?: string;
  /**
   * Optional explicit agent key - bypasses module-based agent selection.
   * Use for targeted agent invocations (e.g. 'sales_company_enrichment').
   */
  agentKey?: string;
  /**
   * Controls execution-progress detail. Defaults to 'scoped' for single-purpose
   * button agents (enrich / transcript / generate) and 'full' otherwise. Pass an
   * explicit value to override the auto-derivation.
   */
  executionDisplayMode?: 'full' | 'scoped';
  /**
   * Slash command key (SLASH_COMMANDS). The server validates it (existence +
   * admin gate) and routes deterministically to the command's skill.
   */
  commandKey?: string;
  /**
   * Optional per-run event observer. Called for every server event whose runId
   * matches the run started by this sendRun call. Cleaned up automatically on
   * run_completed / run_error / run_cancelled.
   */
  onRunEvent?: (event: ServerEvent) => void;
  /**
   * Explicit target session for this run. When set, sendRun skips the
   * override-run branch and sends to this session - used by the landing hero,
   * which awaits createNewSession() and must not read the (stale) activeSessionUuid
   * from stateRef before React re-renders.
   */
  sessionUuid?: string;
  /**
   * Optional async gate called after the optimistic user turn is shown but
   * before the `start_run` WS message is sent. Return `false` to abort the
   * run - the optimistic user bubble will be removed. Use this to defer
   * side-effects (e.g. persisting form answers) that must complete before
   * the backend skill reads them, while still showing the user bubble instantly.
   */
  beforeSend?: () => Promise<boolean>;
  /**
   * Precise origin of a button-initiated run (page + exact button label), e.g.
   * { page: 'Deal', label: 'Regenerate' }. Rendered as the source pill under the
   * user bubble and persisted on the chat_message row. Omit for typed messages.
   */
  origin?: RunOrigin;
}

export interface UseChatSessionReturn {
  state: ReturnType<typeof chatReducer>;
  isConnected: boolean;
  isRunActive: boolean;
  sendRun: (opts: SendRunOptions) => void;
  cancelRun: () => void;
  confirmRun: () => void;
  denyRun: () => void;
  /** [Phase 4D] Answer a mid-run human-in-the-loop prompt (confirm/choice/text). */
  respondPrompt: (promptId: string, response: UserPromptResponse) => void;
  retryRun: (opts: SendRunOptions) => void;
  updateContext: (context: SelectedContext) => void;
  addExplicitContext: (item: ExplicitContextItem) => void;
  removeExplicitContext: (itemId: string) => void;
  clearExplicitContext: () => void;
  /** Set the single pending draft context item (replaces any existing draft). */
  setDraftExplicitContext: (item: ExplicitContextItem) => void;
  /** Promote the current draft into confirmed explicitContextItems. */
  confirmDraftExplicitContext: () => void;
  /** Dismiss the current draft without confirming. */
  clearDraftExplicitContext: () => void;
  loadSessions: () => void;
  switchSession: (sessionUuid: string) => void;
  /**
   * Rename a session from the inline header editor. Optimistic - dispatches
   * SESSION_TITLE_UPDATED immediately, then persists via PATCH (fire-and-forget).
   */
  renameSession: (sessionUuid: string, title: string) => void;
  loadOlderHistory: () => void;
  createNewSession: (opts?: { title?: string; mode?: string }) => Promise<string | null>;
  /** Set the composer's Ask/Agent/Auto mode (shared across all composer surfaces). */
  setComposerMode: (mode: ChatMode) => void;
  /**
   * Manually trigger a reconnect for the active session.
   * Resets the attempt counter and opens a fresh WebSocket connection.
   * Call this when auto-reconnect has been exhausted (attempt >= MAX_RECONNECT_ATTEMPTS).
   */
  manualReconnect: () => void;
  /**
   * Optimistically record the user's action on an enrichment card, then persist
   * it to the database. Dispatches SET_ACTION_RESULT immediately; the API call
   * is fire-and-forget (errors are non-fatal - the optimistic state stands).
   */
  setMessageActionResult: (
    runId: string,
    messageUuid: string,
    actionResult: 'accepted' | 'dismissed'
  ) => void;
  /**
   * Synthesise a run_completed event for a run that finished on the server but
   * whose completion events were lost (e.g. WebSocket dropped mid-generation).
   * Clears activeRunId and auto-completes any running plan steps in the drawer.
   * No-op if runId is not the current activeRunId.
   */
  reconcileStalledRun: (runId: string) => void;
}

// Maximum number of automatic reconnect attempts before requiring manual intervention.
const MAX_RECONNECT_ATTEMPTS = 5;
// Reconnect interval in ms - fixed frequency for predictable UX.
const RECONNECT_INTERVAL_MS = 3_000;

/**
 * Scan a structured_payload event's payload for a navigation action.
 * Returns the first NavigationActionPayload found, else null.
 */
function extractNavigationAction(payload: unknown): NavigationActionPayload | null {
  const actions = (payload as { actions?: Array<{ payload?: unknown }> } | null)?.actions;
  if (!Array.isArray(actions)) return null;
  for (const action of actions) {
    if (isNavigationActionPayload(action?.payload)) return action.payload;
  }
  return null;
}

export interface UseChatSessionOptions {
  /**
   * Called when a LIVE structured_payload event carries a navigation suggestion
   * and the run's mode warrants auto-navigation ('agent', or 'auto' with the
   * AI's autoNavigate flag). Never fires for history rehydration or replays of
   * runs not started in this page lifetime.
   */
  onAutoNavigate?: (payload: NavigationActionPayload) => void;
}

export function useChatSession(
  defaultContext?: SelectedContext,
  options?: UseChatSessionOptions
): UseChatSessionReturn {
  const { user } = useAuth();
  const { activeModuleKey, workspace } = useWorkspace();
  const maxBackgroundSessions = workspace?.aiChatMaxBackgroundSessions ?? 4;
  const [state, dispatch] = React.useReducer(chatReducer, {
    ...initialChatState,
    selectedContext: defaultContext ?? {},
  });

  // Sync defaultContext into selectedContext when it changes after async load.
  // e.g. currentProject loads after mount → chatDefaultContext.projectUuid goes
  // from undefined → actual UUID. Without this the session would send runs with
  // no projectUuid, causing the server to skip repo_copilot and omit retrieval.
  // workspaceUuid is treated the same way for module-scoped (sales) sessions.
  React.useEffect(() => {
    if (defaultContext?.projectUuid) {
      dispatch({ type: 'SET_CONTEXT', context: defaultContext });
    }
  }, [defaultContext?.projectUuid]);

  React.useEffect(() => {
    if (defaultContext?.workspaceUuid) {
      dispatch({ type: 'SET_CONTEXT', context: defaultContext });
    }
  }, [defaultContext?.workspaceUuid]);

  // Per-session WS connection pool. Each session UUID maps to its own ChatWsClient.
  // Active session's client is the one used for sendRun / cancelRun / etc.
  // Background sessions keep their clients alive while runs are in-flight.
  const clientPool = React.useRef<Map<string, ChatWsClient>>(new Map());
  // Keep a stable ref to state for callbacks
  const stateRef = React.useRef(state);
  // Keep a stable ref to activeModuleKey for callbacks
  const activeModuleKeyRef = React.useRef(activeModuleKey);
  React.useEffect(() => {
    activeModuleKeyRef.current = activeModuleKey;
  }, [activeModuleKey]);

  React.useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ── AI-driven navigation (agent/auto modes) ────────────────────────────────
  // Mode of each run STARTED BY THIS CLIENT, captured at send time - replays of
  // runs from previous page lifetimes are absent here and can never auto-navigate.
  const localRunModes = React.useRef<Map<string, ChatMode>>(new Map());
  // Runs that already triggered a navigation (replay dedupe; page-lifetime).
  const navigatedRuns = React.useRef<Set<string>>(new Set());
  const optionsRef = React.useRef(options);
  optionsRef.current = options;
  // Per-run event observers registered via sendRun({ onRunEvent }). Keyed by runId.
  const runEventListeners = React.useRef<Map<string, (event: ServerEvent) => void>>(new Map());
  // Stable ref to defaultContext so onEvent callbacks can read it without stale closures.
  const defaultContextRef = React.useRef(defaultContext);
  React.useEffect(() => {
    defaultContextRef.current = defaultContext;
  }, [defaultContext]);

  // Per-session reconnect state (attempt counter + pending timer).
  const mountedRef = React.useRef(true);
  const reconnectStateRef = React.useRef<
    Map<string, { attempt: number; timer: ReturnType<typeof setTimeout> | null }>
  >(new Map());

  // ── Per-session WS lifecycle ──────────────────────────────────────────────

  /**
   * Open (or reuse) a WS connection dedicated to a single session UUID.
   * The connection is bound to the session at the URL level (?sessionUuid=…)
   * so the server resolves dbSessionId eagerly at connect time.
   *
   * isReconnect=true forces a fresh Firebase ID token.
   */
  async function connectForSession(
    sessionUuid: string,
    isReconnect = false
  ): Promise<ChatWsClient | null> {
    if (!mountedRef.current) return null;
    // Return existing client if already in the pool and still alive.
    const existing = clientPool.current.get(sessionUuid);
    if (existing) {
      // OPEN or CONNECTING - alive, reuse as-is so queued commands are preserved.
      // Re-sync the connection status to the live socket before returning: the caller
      // (switchSession / createNewSession) just reset connectionStatus to 'idle', and
      // reusing an already-open socket would otherwise never re-emit 'connected',
      // leaving the composer stuck on "Connecting…" (input + slash disabled) while the
      // socket is actually live. The reducer only mutates the GLOBAL status when this
      // is the active session, so this is a no-op for background sessions.
      if (existing.readyState === WebSocket.OPEN) {
        if (mountedRef.current) {
          dispatch({ type: 'CONNECTION_STATUS', sessionUuid, status: 'connected' });
        }
        return existing;
      }
      if (existing.readyState === WebSocket.CONNECTING) {
        if (mountedRef.current) {
          dispatch({ type: 'CONNECTION_STATUS', sessionUuid, status: 'connecting' });
        }
        return existing;
      }
      // CLOSING or CLOSED - socket is dying or dead; evict and create a fresh one
      // so queued commands aren't lost in a socket that will never deliver them.
      // Also cancel any pending reconnect timer so it doesn't later delete the new
      // client we're about to create.
      const rs = reconnectStateRef.current.get(sessionUuid);
      if (rs?.timer) clearTimeout(rs.timer);
      reconnectStateRef.current.set(sessionUuid, { attempt: rs?.attempt ?? 0, timer: null });
      clientPool.current.delete(sessionUuid);
    }

    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) return null;
    let idToken: string;
    try {
      idToken = await firebaseUser.getIdToken(isReconnect);
    } catch {
      if (mountedRef.current) {
        dispatch({ type: 'CONNECTION_STATUS', sessionUuid, status: 'error' });
      }
      return null;
    }

    if (!mountedRef.current) return null;
    // Guard: another call may have added the client while we awaited the token.
    const existingAfterToken = clientPool.current.get(sessionUuid);
    if (existingAfterToken) return existingAfterToken;

    dispatch({ type: 'CONNECTION_STATUS', sessionUuid, status: 'connecting' });

    const client = new ChatWsClient({
      wsBaseUrl: resolveWsBaseUrl(),
      idToken,
      sessionUuid,
      onEvent: (event) => {
        dispatch({ type: 'SERVER_EVENT', sessionUuid, event });

        // Fire per-run observers registered via sendRun({ onRunEvent }).
        const eventWithRunId = event as { runId?: string };
        if (eventWithRunId.runId) {
          const cb = runEventListeners.current.get(eventWithRunId.runId);
          if (cb) {
            cb(event);
            if (
              event.type === 'run_completed' ||
              event.type === 'run_error' ||
              event.type === 'run_cancelled'
            ) {
              runEventListeners.current.delete(eventWithRunId.runId);
            }
          }
        }

        // AI-driven navigation: live structured_payload from a run this client
        // started, in agent mode (or auto mode when the AI flagged navigateNow).
        if (event.type === 'structured_payload') {
          const runMode = localRunModes.current.get(event.runId);
          if (runMode && !navigatedRuns.current.has(event.runId)) {
            const nav = extractNavigationAction(event.payload);
            if (nav && (runMode === 'agent' || (runMode === 'auto' && nav.autoNavigate))) {
              navigatedRuns.current.add(event.runId);
              optionsRef.current?.onAutoNavigate?.(nav);
            }
          }
        }
        if (
          event.type === 'run_completed' ||
          event.type === 'run_error' ||
          event.type === 'run_cancelled'
        ) {
          localRunModes.current.delete((event as { runId?: string }).runId ?? '');
        }

        // Auto-close background session clients once their run finishes.
        if (
          (event.type === 'run_completed' ||
            event.type === 'run_error' ||
            event.type === 'run_cancelled') &&
          stateRef.current.activeSessionUuid !== sessionUuid
        ) {
          setTimeout(() => {
            // Double-check: still background and no new run started.
            if (stateRef.current.activeSessionUuid !== sessionUuid) {
              const c = clientPool.current.get(sessionUuid);
              if (c) {
                c.disconnect();
                clientPool.current.delete(sessionUuid);
              }
            }
          }, 250);
        }

        // Title inference runs server-side on every new session (canonical
        // session_title_generation agent/skill); the result is broadcast as a
        // session_title_updated event. Update the session list in place.
        if (event.type === 'session_title_updated') {
          dispatch({
            type: 'SESSION_TITLE_UPDATED',
            sessionUuid: event.sessionUuid,
            title: event.title,
          });
        }
      },
      onStatus: (status) => {
        dispatch({ type: 'CONNECTION_STATUS', sessionUuid, status });

        if (status === 'connected') {
          const rs = reconnectStateRef.current.get(sessionUuid);
          const wasReconnect = (rs?.attempt ?? 0) > 0;
          if (rs?.timer) clearTimeout(rs.timer);
          reconnectStateRef.current.set(sessionUuid, { attempt: 0, timer: null });
          dispatch({ type: 'RECONNECT_ATTEMPT', sessionUuid, attempt: 0 });

          // [Phase 3] On RE-connect (not the first connect), if a run was in flight,
          // re-subscribe to it: the server replays events we missed (runSeq > lastSeq)
          // then live-tails. The run kept executing server-side (no longer aborted on
          // disconnect), so this seamlessly resumes it instead of faking completion.
          if (wasReconnect) {
            const isActive = stateRef.current.activeSessionUuid === sessionUuid;
            const slice = isActive ? stateRef.current : stateRef.current.sessionPool[sessionUuid];
            const runId = slice?.activeRunId ?? null;
            if (runId) {
              const client = clientPool.current.get(sessionUuid);
              client?.send({
                type: 'subscribe_run',
                conversationId: slice?.conversationId ?? '',
                ts: new Date().toISOString(),
                runId,
                lastSeq: slice?.lastRunSeq ?? 0,
              });
            }
          }
        } else if (status === 'disconnected') {
          // Only auto-reconnect the active session.
          if (stateRef.current.activeSessionUuid === sessionUuid && mountedRef.current) {
            // If sendRun (or another path) already created a fresh CONNECTING client
            // while this onclose was still in-flight (e.g., user submitted just as the
            // WS was closing), skip scheduling a redundant timer that would otherwise
            // blindly delete the good new client.
            const liveClient = clientPool.current.get(sessionUuid);
            if (
              liveClient &&
              (liveClient.readyState === WebSocket.OPEN ||
                liveClient.readyState === WebSocket.CONNECTING)
            ) {
              return;
            }
            const existing = reconnectStateRef.current.get(sessionUuid) ?? {
              attempt: 0,
              timer: null,
            };
            if (existing.attempt < MAX_RECONNECT_ATTEMPTS) {
              const newAttempt = existing.attempt + 1;
              const timer = setTimeout(() => {
                // Belt-and-suspenders: if a live client appeared in the pool since
                // the timer was scheduled (e.g., a concurrent sendRun call beat us
                // here), don't discard it.
                const currentClient = clientPool.current.get(sessionUuid);
                if (
                  currentClient &&
                  (currentClient.readyState === WebSocket.OPEN ||
                    currentClient.readyState === WebSocket.CONNECTING)
                ) {
                  return;
                }
                // Remove stale client entry so connectForSession can create a fresh one.
                clientPool.current.delete(sessionUuid);
                void connectForSession(sessionUuid, true);
              }, RECONNECT_INTERVAL_MS);
              reconnectStateRef.current.set(sessionUuid, { attempt: newAttempt, timer });
              dispatch({ type: 'RECONNECT_ATTEMPT', sessionUuid, attempt: newAttempt });
            }
            // If attempt >= MAX_RECONNECT_ATTEMPTS: stop auto-reconnect, leave status as
            // 'disconnected' with attempt at MAX - UI shows a manual Reconnect button.
          }
        }
        // 'error' status - do not reconnect (e.g. auth rejection).
      },
    });

    clientPool.current.set(sessionUuid, client);
    client.connect();
    return client;
  }

  /**
   * Close a session's WS client if it has no in-flight run.
   * Background sessions with active runs are left alive until the run completes.
   * Also evicts the oldest idle background client when the pool exceeds
   * maxBackgroundSessions (sourced from workspace.config ai.chat_max_background_sessions,
   * default 4).
   */
  function closeIdleClient(sessionUuid: string): void {
    const slice =
      sessionUuid === stateRef.current.activeSessionUuid
        ? stateRef.current
        : stateRef.current.sessionPool[sessionUuid];
    if (slice?.activeRunId) return; // run in-flight - keep alive

    const rs = reconnectStateRef.current.get(sessionUuid);
    if (rs?.timer) clearTimeout(rs.timer);
    reconnectStateRef.current.delete(sessionUuid);

    const c = clientPool.current.get(sessionUuid);
    if (c) {
      c.disconnect();
      clientPool.current.delete(sessionUuid);
    }

    // Evict oldest idle background client if pool is at or above cap.
    // Active session socket is never evicted here.
    if (clientPool.current.size >= maxBackgroundSessions) {
      for (const [uuid, client] of clientPool.current) {
        if (uuid === stateRef.current.activeSessionUuid) continue;
        const s = stateRef.current.sessionPool[uuid];
        if (!s?.activeRunId) {
          const evictRs = reconnectStateRef.current.get(uuid);
          if (evictRs?.timer) clearTimeout(evictRs.timer);
          reconnectStateRef.current.delete(uuid);
          client.disconnect();
          clientPool.current.delete(uuid);
          break;
        }
      }
    }
  }

  // ── Disconnect all clients on logout / unmount ────────────────────────────
  React.useEffect(() => {
    if (!user) {
      // Logged out - disconnect all open sessions immediately.
      for (const [, rs] of reconnectStateRef.current) {
        if (rs.timer) clearTimeout(rs.timer);
      }
      reconnectStateRef.current.clear();
      for (const [, client] of clientPool.current) {
        client.disconnect();
      }
      clientPool.current.clear();
      dispatch({ type: 'RESET' });
    }

    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      for (const [, rs] of reconnectStateRef.current) {
        if (rs.timer) clearTimeout(rs.timer);
      }
      reconnectStateRef.current.clear();
      for (const [, client] of clientPool.current) {
        client.disconnect();
      }
      clientPool.current.clear();
    };
  }, [user]);

  // ── Load sessions list on mount, then auto-select most recent session ─────────
  // SESSIONS_LOADED populates the dropdown. The subsequent auto-select effect
  // (below) fires switchSession(sessions[0]) so the panel shows the most recent
  // session's messages without requiring a manual dropdown interaction.
  React.useEffect(() => {
    if (!user || (!defaultContext?.projectUuid && !defaultContext?.workspaceUuid)) return;
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, defaultContext?.projectUuid, defaultContext?.workspaceUuid]);

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Create a new chat session via REST and update local state.
   *
   * Context resolution order (first wins):
   *   1. Explicit `ctx` argument (used by sendRun which captures effectiveContext)
   *   2. stateRef.current.selectedContext (used by createNewSession button)
   *
   * `skipClearTurns` - pass true when called from sendRun so the user's
   * in-flight message stays visible while waiting for the AI response.
   *
   * Returns the new session UUID on success, null on failure.
   */
  async function createSession(opts?: {
    ctx?: SelectedContext;
    title?: string;
    mode?: string;
    skipClearTurns?: boolean;
  }): Promise<string | null> {
    const ctx = opts?.ctx ?? stateRef.current.selectedContext;
    const scope = resolveScope(ctx.projectUuid, ctx.workspaceUuid);
    if (!scope) return null;

    const idToken = await getAuthToken();
    if (!idToken) return null;

    try {
      const session = await apiCreateSession(
        scope,
        { title: opts?.title ?? null, mode: opts?.mode },
        idToken
      );
      dispatch({ type: 'SESSION_CREATED', session, skipClearTurns: opts?.skipClearTurns });
      // Open a dedicated WS connection for the new session.
      connectForSession(session.uuid);
      return session.uuid;
    } catch {
      return null;
    }
  }

  const sendRun = React.useCallback(
    ({
      prompt,
      context,
      model,
      mode,
      moduleKey,
      agentKey,
      executionDisplayMode,
      commandKey,
      onRunEvent,
      beforeSend,
      sessionUuid: explicitSessionUuid,
      origin,
    }: SendRunOptions) => {
      const currentState = stateRef.current;

      // Single-purpose button agents default to 'scoped' display (suppress internal
      // pipeline/history steps). Explicit executionDisplayMode wins.
      const SCOPED_AGENT_KEYS = new Set([
        'sales_company_enrichment',
        'deal_transcript_analysis',
        'sales_deal_generation',
        'regenerate_estimate',
        'regenerate_sow',
      ]);
      const resolvedDisplayMode: 'full' | 'scoped' =
        executionDisplayMode ?? (agentKey && SCOPED_AGENT_KEYS.has(agentKey) ? 'scoped' : 'full');

      // Per-run mode: explicit caller value wins; otherwise fall back to the active
      // session's stored default/last-used mode; otherwise 'ask'. (Per-message mode
      // is persisted on chat_message.chat_mode; chat_session.mode is the seed.)
      const activeSession = currentState.sessions.find(
        (s) => s.uuid === currentState.activeSessionUuid
      );
      const effectiveMode: ChatMode =
        mode ?? currentState.composerMode ?? (activeSession?.mode as ChatMode) ?? 'ask';

      // Merge caller-supplied context on top of session's selectedContext so
      // projectUuid/workspaceUuid are always present even when the caller passes
      // only partial overrides (e.g. { pageScope }). Then apply explicit context
      // items additively so user-selected files/dirs are included in every run.
      const baseContext: SelectedContext = {
        ...currentState.selectedContext,
        ...(context ?? {}),
      };
      const allItems = currentState.draftExplicitItem
        ? [...currentState.explicitContextItems, currentState.draftExplicitItem]
        : currentState.explicitContextItems;
      const effectiveContext = mergeExplicitItemsIntoContext(baseContext, allItems);
      const conversationId = currentState.conversationId ?? '';
      const ts = new Date().toISOString();

      if (currentState.activeRunId && !explicitSessionUuid) {
        // Override: cancel first, then start new run. Needs an existing WS client.
        // Skipped when an explicit target session is given (landing fresh-session
        // flow) - a stale activeRunId from the departing session must not hijack it.
        const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
        if (!client) return;

        const newRunId = crypto.randomUUID();
        if (onRunEvent) runEventListeners.current.set(newRunId, onRunEvent);
        localRunModes.current.set(newRunId, effectiveMode);
        const activeRunId = currentState.activeRunId;
        dispatch({
          type: 'USER_TURN_SENT',
          runId: newRunId,
          prompt,
          context: effectiveContext,
          triggerSource: triggerSourceForAgentKey(agentKey),
          origin,
        });

        client.send({
          type: 'override_run',
          conversationId,
          ts,
          activeRunId,
          newRunId,
          prompt,
          context: effectiveContext,
          model,
          mode: effectiveMode,
          // Preserve explicit routing so a button/command run started while another run
          // is active still reaches its agent/skill instead of the copilot classifier.
          agentKey,
          executionDisplayMode: resolvedDisplayMode,
          ...(commandKey ? { commandKey } : {}),
          ...(origin ? { origin } : {}),
        });
      } else {
        const runId = crypto.randomUUID();
        if (onRunEvent) runEventListeners.current.set(runId, onRunEvent);
        localRunModes.current.set(runId, effectiveMode);
        dispatch({
          type: 'USER_TURN_SENT',
          runId,
          prompt,
          context: effectiveContext,
          triggerSource: triggerSourceForAgentKey(agentKey),
          origin,
        });

        // When a beforeSend gate is present (e.g. to persist form data before
        // the skill reads it), optimistically add the AssistantRun placeholder
        // immediately so the agent bubble appears while the persist is in-flight.
        // The real run_started from the server will update model/mode in-place.
        if (beforeSend) {
          dispatch({
            type: 'OPTIMISTIC_RUN_PENDING',
            runId,
            model: model ?? ('' as GeminiModel),
            mode: effectiveMode,
          });
        }

        void (async () => {
          // If no active session, create one so the dropdown label updates
          // immediately. Pass effectiveContext so workspaceUuid/projectUuid
          // is taken from the run's context (reliable even when selectedContext
          // is still empty because the workspace loaded after mount).
          let sessionUuid = explicitSessionUuid ?? stateRef.current.activeSessionUuid ?? undefined;
          if (!sessionUuid) {
            try {
              const newUuid = await createSession({ ctx: effectiveContext, skipClearTurns: true });
              sessionUuid = newUuid ?? undefined;
            } catch {
              // Non-fatal - WS server auto-creates a DB session as fallback
            }
          }

          const targetUuid = explicitSessionUuid ?? stateRef.current.activeSessionUuid;
          if (!targetUuid) return;
          // connectForSession is now async - await it to get (or establish) the WS client.
          // This handles the race where the WS token fetch hasn't completed yet after
          // createSession() returned.
          const c = await connectForSession(targetUuid);
          if (!c) return;

          // If the caller supplied a beforeSend gate (e.g. to persist form data
          // before the skill reads it), await it now. Returning false aborts the
          // run and removes the optimistic user bubble shown by USER_TURN_SENT.
          if (beforeSend) {
            const ok = await beforeSend();
            if (!ok) {
              dispatch({ type: 'USER_TURN_ABORTED', runId });
              return;
            }
          }

          c.send({
            type: 'start_run',
            conversationId: stateRef.current.conversationId ?? '',
            ts: new Date().toISOString(),
            runId,
            prompt,
            context: effectiveContext,
            model,
            moduleKey: moduleKey ?? activeModuleKeyRef.current ?? undefined,
            agentKey,
            executionDisplayMode: resolvedDisplayMode,
            ...(commandKey ? { commandKey } : {}),
            ...(origin ? { origin } : {}),
            mode: effectiveMode,
            sessionUuid,
          });
        })();
      }
    },
    []
  );

  const cancelRun = React.useCallback(() => {
    const currentState = stateRef.current;
    const runId = currentState.activeRunId;
    const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
    if (!client || !runId) return;

    const ts = new Date().toISOString();
    // Optimistically revert Stop → Send and mark the run cancelled immediately
    // without waiting for the server's run_cancelled ack.
    dispatch({ type: 'CANCEL_RUN_OPTIMISTIC', runId, ts });
    client.send({
      type: 'cancel_run',
      conversationId: currentState.conversationId ?? '',
      ts,
      runId,
    });
  }, []);

  const confirmRun = React.useCallback(() => {
    const currentState = stateRef.current;
    const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
    if (!client || !currentState.activeRunId) return;

    client.send({
      type: 'confirm',
      conversationId: currentState.conversationId ?? '',
      ts: new Date().toISOString(),
      runId: currentState.activeRunId,
    });
  }, []);

  const denyRun = React.useCallback(() => {
    const currentState = stateRef.current;
    const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
    if (!client || !currentState.activeRunId) return;

    client.send({
      type: 'deny',
      conversationId: currentState.conversationId ?? '',
      ts: new Date().toISOString(),
      runId: currentState.activeRunId,
    });
  }, []);

  // [Phase 4D] Answer a mid-run human-in-the-loop prompt (confirm/choice/text).
  const respondPrompt = React.useCallback((promptId: string, response: UserPromptResponse) => {
    const currentState = stateRef.current;
    const runId = currentState.activeRunId;
    const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
    if (!client || !runId) return;
    client.send({
      type: 'respond_prompt',
      conversationId: currentState.conversationId ?? '',
      ts: new Date().toISOString(),
      runId,
      promptId,
      response,
    });
    // Navigation (Back / Change answer) is not an answer: clear the pending card
    // and wait for the skill's re-ask - the question segment stays unanswered.
    if (response.action === 'back' || response.action === 'revise') {
      dispatch({
        type: 'PROMPT_ANSWERED',
        runId,
        promptId,
        answer: '',
        navigation: response.action,
      });
      return;
    }
    // Local echo: record the answer on the run's transcript and clear the pending
    // card immediately instead of waiting for the next server event.
    const run = currentState.turns.find((t) => t.kind === 'assistant' && t.runId === runId);
    const prompt =
      run?.kind === 'assistant' && run.pendingPrompt?.promptId === promptId
        ? run.pendingPrompt
        : undefined;
    // Single-sourced with the server-side transcript builder (derivePromptAnswer in
    // @protoss/shared) so the rendered answer is identical live and on history reload.
    const derived = derivePromptAnswer(prompt, response);
    const answerPreviews = deriveSelectedPreviews(prompt, response);
    dispatch({
      type: 'PROMPT_ANSWERED',
      runId,
      promptId,
      answer: derived.answer,
      ...(derived.formAnswers ? { formAnswers: derived.formAnswers } : {}),
      ...(derived.answerAssetUuid
        ? { assetUuid: derived.answerAssetUuid, assetUuids: derived.answerAssetUuids }
        : {}),
      ...(answerPreviews.length ? { answerPreviews } : {}),
    });
  }, []);

  const retryRun = React.useCallback(({ prompt, context, model, mode }: SendRunOptions) => {
    const currentState = stateRef.current;
    const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
    if (!client || currentState.activeRunId) return;

    // Find the last failed run
    const failedRun = [...currentState.turns]
      .reverse()
      .find((t) => t.kind === 'assistant' && t.lifecycleState === 'error');
    const failedRunId = failedRun?.kind === 'assistant' ? failedRun.runId : '';

    const newRunId = crypto.randomUUID();
    localRunModes.current.set(newRunId, mode ?? currentState.composerMode ?? 'ask');
    // Merge caller-supplied context with current selectedContext and explicit items
    // so the retry benefits from any context selections made since the original run.
    const baseContext: SelectedContext = {
      ...currentState.selectedContext,
      ...(context ?? {}),
    };
    const allItems = currentState.draftExplicitItem
      ? [...currentState.explicitContextItems, currentState.draftExplicitItem]
      : currentState.explicitContextItems;
    const effectiveContext = mergeExplicitItemsIntoContext(baseContext, allItems);

    dispatch({
      type: 'USER_TURN_SENT',
      runId: newRunId,
      prompt,
      context: effectiveContext,
      triggerSource: 'user_retry',
    });

    client.send({
      type: 'retry_run',
      conversationId: currentState.conversationId ?? '',
      ts: new Date().toISOString(),
      failedRunId,
      newRunId,
      prompt,
      context: effectiveContext,
      model,
      mode,
    });
  }, []);

  const updateContext = React.useCallback((context: SelectedContext) => {
    dispatch({ type: 'SET_CONTEXT', context });

    const currentState = stateRef.current;
    const client = clientPool.current.get(currentState.activeSessionUuid ?? '');
    if (client && currentState.conversationId) {
      client.send({
        type: 'update_selected_context',
        conversationId: currentState.conversationId,
        ts: new Date().toISOString(),
        context,
      });
    }
  }, []);

  // ── Explicit context management ───────────────────────────────────────────

  const addExplicitContext = React.useCallback((item: ExplicitContextItem) => {
    dispatch({ type: 'ADD_EXPLICIT_CONTEXT', item });
  }, []);

  const removeExplicitContext = React.useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_EXPLICIT_CONTEXT', itemId });
  }, []);

  const clearExplicitContext = React.useCallback(() => {
    dispatch({ type: 'CLEAR_EXPLICIT_CONTEXT' });
  }, []);

  const setDraftExplicitContext = React.useCallback((item: ExplicitContextItem) => {
    dispatch({ type: 'SET_DRAFT_EXPLICIT_CONTEXT', item });
  }, []);

  const confirmDraftExplicitContext = React.useCallback(() => {
    dispatch({ type: 'CONFIRM_DRAFT_EXPLICIT_CONTEXT' });
  }, []);

  const clearDraftExplicitContext = React.useCallback(() => {
    dispatch({ type: 'CLEAR_DRAFT_EXPLICIT_CONTEXT' });
  }, []);

  // ── Session management (REST API) ─────────────────────────────────────────

  /**
   * Helper for authenticated REST calls; returns null on auth failure.
   */
  async function getAuthToken(): Promise<string | null> {
    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) return null;
    try {
      return await firebaseUser.getIdToken();
    } catch {
      return null;
    }
  }

  const loadSessions = React.useCallback(() => {
    const scope = resolveScope(defaultContext?.projectUuid, defaultContext?.workspaceUuid);
    if (!user || !scope) return;

    dispatch({ type: 'SESSIONS_STATUS', status: 'loading' });

    void (async () => {
      const idToken = await getAuthToken();
      if (!idToken) {
        dispatch({ type: 'SESSIONS_STATUS', status: 'error' });
        return;
      }
      try {
        const sessions = await apiListSessions(scope, idToken);
        dispatch({ type: 'SESSIONS_LOADED', sessions });
      } catch {
        dispatch({ type: 'SESSIONS_STATUS', status: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, defaultContext?.projectUuid, defaultContext?.workspaceUuid]);

  const renameSession = React.useCallback(
    (sessionUuid: string, title: string) => {
      const scope = resolveScope(defaultContext?.projectUuid, defaultContext?.workspaceUuid);
      const trimmed = title.trim();
      if (!scope || !trimmed) return;

      // Optimistic: the header/list update immediately; the API call is
      // fire-and-forget (errors are non-fatal - the optimistic state stands
      // until the next sessions load).
      dispatch({ type: 'SESSION_TITLE_UPDATED', sessionUuid, title: trimmed });

      void (async () => {
        const idToken = await getAuthToken();
        if (!idToken) return;
        try {
          await apiRenameSession(scope, sessionUuid, trimmed, idToken);
        } catch (err) {
          console.error('[ai-chat] rename session failed', sessionUuid, err);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultContext?.projectUuid, defaultContext?.workspaceUuid]
  );

  // ── Shared helper: fetch a page of session history and convert to turns ──────

  const switchSession = React.useCallback(
    (sessionUuid: string) => {
      const scope = resolveScope(defaultContext?.projectUuid, defaultContext?.workspaceUuid);
      if (!user || !scope) return;

      // Pre-check pool: if we have live state for this session the reducer will
      // restore it immediately from the pool (no blank loading screen).
      const hasPoolEntry = Boolean(stateRef.current.sessionPool[sessionUuid]);

      // Dispatch: saves current session's slice to pool, restores target from pool or blanks.
      dispatch({ type: 'SESSION_SWITCH_STARTED', sessionUuid });

      // Open a dedicated WS connection for this session (no-op if already open).
      const prevUuid = stateRef.current.activeSessionUuid;
      connectForSession(sessionUuid);

      // Close the departing session's client when it has no in-flight run.
      if (prevUuid && prevUuid !== sessionUuid) {
        closeIdleClient(prevUuid);
      }

      void (async () => {
        const idToken = await getAuthToken();
        if (!idToken) {
          if (!hasPoolEntry) dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'error' });
          return;
        }

        try {
          const data = await apiLoadSessionHistory(scope, sessionUuid, idToken, { limit: 50 });

          // If DB has messages, or we have no live pool state, replace with canonical DB view.
          if (data.messages.length > 0 || !hasPoolEntry) {
            const turns = messagesToTurns(
              data.messages,
              defaultContext?.projectUuid ?? defaultContext?.workspaceUuid ?? ''
            );
            const oldestMessageIndex =
              data.messages.length > 0 ? (data.messages[0]?.messageIndex ?? null) : null;

            dispatch({
              type: 'SESSION_SWITCHED',
              sessionUuid,
              turns,
              hasMore: data.hasMore,
              oldestMessageIndex,
            });
          }
        } catch {
          if (!hasPoolEntry) dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'error' });
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, defaultContext?.projectUuid, defaultContext?.workspaceUuid]
  );

  const loadOlderHistory = React.useCallback(() => {
    const scope = resolveScope(defaultContext?.projectUuid, defaultContext?.workspaceUuid);
    const currentState = stateRef.current;
    if (!user || !scope) return;
    if (!currentState.activeSessionUuid) return;
    if (!currentState.hasMoreHistory) return;
    if (currentState.historyStatus === 'loading') return;

    const sessionUuid = currentState.activeSessionUuid;
    const before = currentState.oldestMessageIndex;

    dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'loading' });

    void (async () => {
      const idToken = await getAuthToken();
      if (!idToken) {
        dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'error' });
        return;
      }

      try {
        const data = await apiLoadSessionHistory(scope, sessionUuid, idToken, {
          limit: 50,
          before,
        });

        const turns = messagesToTurns(
          data.messages,
          defaultContext?.projectUuid ?? defaultContext?.workspaceUuid ?? ''
        );
        const oldestMessageIndex =
          data.messages.length > 0 ? (data.messages[0]?.messageIndex ?? null) : null;

        dispatch({
          type: 'OLDER_HISTORY_LOADED',
          turns,
          hasMore: data.hasMore,
          oldestMessageIndex,
        });
      } catch {
        dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, defaultContext?.projectUuid, defaultContext?.workspaceUuid]);

  const createNewSession = React.useCallback(
    async (opts?: { title?: string; mode?: string }): Promise<string | null> => {
      if (!user) return null;

      // Read from stateRef so this always uses the current context regardless
      // of when the callback was last recreated. Using stable refs means the
      // only dep we need is `user`.
      const ctx = stateRef.current.selectedContext;
      const scope = resolveScope(ctx.projectUuid, ctx.workspaceUuid);
      if (!scope) return null;

      const idToken = await getAuthToken();
      if (!idToken) return null;

      try {
        const session = await apiCreateSession(
          scope,
          { title: opts?.title ?? null, mode: opts?.mode },
          idToken
        );
        // No skipClearTurns: clicking "+ New session" intentionally starts a blank pane.
        dispatch({ type: 'SESSION_CREATED', session });
        // Open a dedicated WS connection for the new session.
        connectForSession(session.uuid);
        return session.uuid;
      } catch {
        // Non-fatal
        return null;
      }
    },
    [user]
  );

  const setComposerMode = React.useCallback((m: ChatMode) => {
    dispatch({ type: 'SET_COMPOSER_MODE', mode: m });
  }, []);

  // ── Auto-select the most recent session after sessions first load ─────────
  // Replaces the old cross-session loadHistory approach. When sessions become
  // available and no session is currently active, automatically switch to the
  // newest one so the chat panel is populated without any manual interaction.
  React.useEffect(() => {
    if (
      state.sessionsStatus === 'loaded' &&
      state.sessions.length > 0 &&
      state.activeSessionUuid === null &&
      state.historyStatus !== 'loading'
    ) {
      const first = state.sessions[0];
      if (first) switchSession(first.uuid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sessionsStatus, state.sessions.length, state.activeSessionUuid, switchSession]);

  // ── Cold-resume an in-flight run after a hard refresh ─────────────────────
  // The session list advertises `activeRunId` for any session whose AI run is still
  // executing server-side. A hard refresh wipes the in-memory run, and the normal
  // subscribe_run replay only fires on a socket RE-connect - never on this first cold
  // connect. So when the active session has an advertised activeRunId and nothing is
  // currently attached, re-attach via subscribe_run (lastSeq 0 → full replay): the server
  // replays the durable run_started/plan_step/message events, which re-create the
  // assistant run and live-tail it to completion instead of leaving a blank panel.
  // SESSION_SWITCHED merges in-flight turns, so this is safe regardless of ordering.
  const resumedRunIdsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const sessionUuid = state.activeSessionUuid;
    if (!sessionUuid) return;
    if (state.connectionStatus !== 'connected') return;
    if (state.activeRunId) return; // a run is already active/attached in this view
    const summary = state.sessions.find((s) => s.uuid === sessionUuid);
    const runId = summary?.activeRunId ?? null;
    if (!runId || resumedRunIdsRef.current.has(runId)) return;
    // Already rendering this run (e.g. it completed and history hydrated it)? Skip.
    if (stateRef.current.turns.some((t) => t.kind === 'assistant' && t.runId === runId)) return;

    resumedRunIdsRef.current.add(runId);
    // Seed the user bubble from the run's original prompt so the conversation reads
    // correctly; the assistant run itself comes from the replayed run_started.
    if (summary?.activeRunPrompt) {
      dispatch({ type: 'USER_TURN_SENT', runId, prompt: summary.activeRunPrompt, context: {} });
    }
    const client = clientPool.current.get(sessionUuid);
    client?.send({
      type: 'subscribe_run',
      conversationId: stateRef.current.conversationId ?? '',
      ts: new Date().toISOString(),
      runId,
      lastSeq: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeSessionUuid, state.connectionStatus, state.activeRunId, state.sessions]);

  // ── Auto-create a session when none exist after first load ────────────────
  // Without this, first-time users (or new projects with no prior sessions) see
  // "Connecting…" forever because connectForSession is never called when
  // sessions.length === 0. Covers all three entry points:
  //   1. Opening the chat pane on a fresh workspace
  //   2. Clicking the Enrich button before any session exists
  //   3. First page load navigating directly to a deal/project page
  React.useEffect(() => {
    if (
      state.sessionsStatus === 'loaded' &&
      state.sessions.length === 0 &&
      state.activeSessionUuid === null
    ) {
      void createNewSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sessionsStatus, state.sessions.length, state.activeSessionUuid]);

  // ── Reconcile a stale connection status against the live socket ───────────
  // Belt-and-suspenders for the desync the user can hit on the landing page:
  // a session switch resets connectionStatus to 'idle', and if the target's
  // pooled socket is already OPEN it is reused without re-emitting 'connected'.
  // The composer then stays disabled ("Connecting…", input + slash blocked)
  // while mode pickers (not gated on isConnected) stay clickable. If the active
  // session's socket is genuinely OPEN, the status must be 'connected'.
  React.useEffect(() => {
    const uuid = state.activeSessionUuid;
    if (!uuid) return;
    const client = clientPool.current.get(uuid);
    if (client?.readyState === WebSocket.OPEN && state.connectionStatus !== 'connected') {
      dispatch({ type: 'CONNECTION_STATUS', sessionUuid: uuid, status: 'connected' });
    }
  }, [state.activeSessionUuid, state.connectionStatus]);

  // ── Retry failed history load when a run completes ────────────────────────
  // When a run finishes (activeRunId transitions non-null → null) and the prior
  // history fetch failed (historyStatus === 'error'), retry loading so the user
  // sees a complete conversation after the run settles.
  //
  // We do NOT refetch when history loaded successfully: the live WS state already
  // contains the full run data (agent steps, streaming text, error details) and
  // is richer than what the DB view can reconstruct. Replacing live state with a
  // DB reload causes visible flicker and discards in-memory-only richness.
  const prevActiveRunIdRef = React.useRef<string | null>(state.activeRunId);
  React.useEffect(() => {
    const prevRunId = prevActiveRunIdRef.current;
    prevActiveRunIdRef.current = state.activeRunId;

    // Only fire when transitioning active run → no active run (completed / cancelled / error).
    if (prevRunId === null || state.activeRunId !== null) return;

    // Skip if history loaded successfully - live state is already the source of truth.
    if (stateRef.current.historyStatus !== 'error') return;

    const sessionUuid = stateRef.current.activeSessionUuid;
    const scope = resolveScope(defaultContext?.projectUuid, defaultContext?.workspaceUuid);
    if (!sessionUuid || !user || !scope) return;

    dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'loading' });

    void (async () => {
      const idToken = await getAuthToken();
      if (!idToken) {
        dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'error' });
        return;
      }
      try {
        const data = await apiLoadSessionHistory(scope, sessionUuid, idToken, { limit: 50 });
        const turns = messagesToTurns(
          data.messages,
          defaultContext?.projectUuid ?? defaultContext?.workspaceUuid ?? ''
        );
        const oldestMessageIndex =
          data.messages.length > 0 ? (data.messages[0]?.messageIndex ?? null) : null;
        dispatch({
          type: 'SESSION_SWITCHED',
          sessionUuid,
          turns,
          hasMore: data.hasMore,
          oldestMessageIndex,
        });
      } catch {
        dispatch({ type: 'HISTORY_STATUS', sessionUuid, status: 'error' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeRunId, user, defaultContext?.projectUuid, defaultContext?.workspaceUuid]);

  /**
   * Manually reconnect the active session's WebSocket.
   * Resets the attempt counter and opens a fresh connection.
   * Intended to be called from the UI after auto-reconnect is exhausted.
   */
  function manualReconnect(): void {
    const sessionUuid = stateRef.current.activeSessionUuid;
    if (!sessionUuid) return;

    // Clear any pending reconnect timer and reset the counter.
    const rs = reconnectStateRef.current.get(sessionUuid);
    if (rs?.timer) clearTimeout(rs.timer);
    reconnectStateRef.current.set(sessionUuid, { attempt: 0, timer: null });

    // Reset the counter in state so the indicator goes back to neutral.
    dispatch({ type: 'RECONNECT_ATTEMPT', sessionUuid, attempt: 0 });

    // Remove the stale client and open a fresh connection.
    clientPool.current.delete(sessionUuid);
    void connectForSession(sessionUuid, true);
  }

  function setMessageActionResult(
    runId: string,
    messageUuid: string,
    actionResult: 'accepted' | 'dismissed',
    acceptedFieldKeys?: string[],
    cardKey?: string
  ): void {
    dispatch({ type: 'SET_ACTION_RESULT', runId, actionResult, acceptedFieldKeys, cardKey });
    // Persist to DB (fire-and-forget)
    void (async () => {
      const ctx = stateRef.current.selectedContext;
      const scope = resolveScope(ctx?.projectUuid, ctx?.workspaceUuid);
      const sessionUuid = stateRef.current.activeSessionUuid;
      if (!scope || !sessionUuid) return;
      const idToken = await getAuthToken();
      if (!idToken) return;
      try {
        await apiSetMessageActionResult(
          scope,
          sessionUuid,
          messageUuid,
          actionResult,
          idToken,
          acceptedFieldKeys,
          cardKey
        );
      } catch (err) {
        // Non-fatal: optimistic state is already shown to the user.
        console.error('[ai-chat] set-action-result failed', messageUuid, err);
      }
    })();
  }

  function reconcileStalledRun(runId: string): void {
    const sessionUuid = stateRef.current.activeSessionUuid;
    if (!sessionUuid) return;
    if (stateRef.current.activeRunId !== runId) return;
    dispatch({
      type: 'SERVER_EVENT',
      sessionUuid,
      event: {
        type: 'run_completed',
        runId,
        conversationId: stateRef.current.conversationId ?? '',
        seq: stateRef.current.lastSeq + 1,
        ts: new Date().toISOString(),
      } as RunCompletedEvent,
    });
  }

  return {
    state,
    isConnected: state.connectionStatus === 'connected',
    isRunActive: !!state.activeRunId,
    sendRun,
    cancelRun,
    confirmRun,
    denyRun,
    respondPrompt,
    retryRun,
    updateContext,
    addExplicitContext,
    removeExplicitContext,
    clearExplicitContext,
    setDraftExplicitContext,
    confirmDraftExplicitContext,
    clearDraftExplicitContext,
    loadSessions,
    switchSession,
    renameSession,
    loadOlderHistory,
    createNewSession,
    setComposerMode,
    manualReconnect,
    setMessageActionResult,
    reconcileStalledRun,
  };
}

// Re-export action type for components that need to dispatch directly
export type { ChatActionType };
